import React, { useMemo, useState } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import * as authMod from "firebase/auth";

import AuthPageFrame from "../../../components/AuthPageFrame";
import Loader from "../../../components/Loader";
import { ModularAuth } from "../../../components/FirebaseAuth/firebase";
import { userUpdateName } from "../../../libs/user";

const cleanAuthError = (error) => {
  const raw = String(error?.message || "").trim();
  const withoutPrefix = raw.replace(/^Firebase:\s*/i, "");
  return withoutPrefix.replace(/\s*\(auth\/[^)]+\)\.?$/i, "") || "Unable to create account.";
};

const SignUp = () => {
  const history = useHistory();
  const [fname, setFname] = useState("");
  const [lname, setLname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState({ severity: "", message: "" });

  const firstNameValid = /^[a-zA-Z]+$/.test(fname.trim());
  const lastNameValid = /^[a-zA-Z]+$/.test(lname.trim());
  const emailValid = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email.trim().toLowerCase());
  const passwordRules = useMemo(() => ([
    { label: "8 to 20 characters", valid: password.length >= 8 && password.length <= 20 },
    { label: "Upper and lowercase letters", valid: /[a-z]/.test(password) && /[A-Z]/.test(password) },
    { label: "A number or symbol", valid: /[0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-]/g.test(password) },
  ]), [password]);
  const passwordValid = passwordRules.every((rule) => rule.valid);
  const formValid = firstNameValid && lastNameValid && emailValid && passwordValid;

  const handleCreateAccount = async (event) => {
    event.preventDefault();
    if (!formValid || loading) return;

    setLoading(true);
    setNotice({ severity: "", message: "" });

    try {
      const userCredential = await authMod.createUserWithEmailAndPassword(ModularAuth, email.trim(), password);
      await authMod.sendEmailVerification(userCredential.user);
      await authMod.updateProfile(userCredential.user, { displayName: `${fname.trim()} ${lname.trim()}` });
      await userUpdateName();
      setNotice({ severity: "success", message: "Account created. Check your inbox to verify your email." });
      setTimeout(() => {
        history.push("/");
      }, 1000);
    } catch (error) {
      setNotice({ severity: "error", message: cleanAuthError(error) });
      setLoading(false);
    }
  };

  const Requirement = ({ valid, children }) => (
    <Stack direction="row" spacing={1} alignItems="center">
      {valid ? (
        <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "success.main" }} />
      ) : (
        <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
      )}
      <Typography variant="body2" color={valid ? "text.primary" : "text.secondary"}>
        {children}
      </Typography>
    </Stack>
  );

  return (
    <AuthPageFrame
      eyebrow="Welcome"
      title="Create account"
      subtitle="Signup and choose the right role that describes you best"
      maxWidth={500}
    >
      <Box component="form" onSubmit={handleCreateAccount}>
        <Stack spacing={1.4}>
          {!!notice.message && <Alert severity={notice.severity || "info"}>{notice.message}</Alert>}

          <Grid container sx={{pt: 1}}>
            <Grid item xs={12} sm={6} sx={{pr: 1}}>
              <TextField
                label="Legal first name"
                size="small"
                value={fname}
                onChange={(event) => setFname(event.target.value)}
                error={!!fname && !firstNameValid}
                helperText={!!fname && !firstNameValid ? "Use letters only." : " "}
                fullWidth
                required
                autoComplete="given-name"
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{pr:0}}>
              <TextField
                label="Legal last name"
                size="small"
                value={lname}
                onChange={(event) => setLname(event.target.value)}
                error={!!lname && !lastNameValid}
                helperText={!!lname && !lastNameValid ? "Use letters only." : " "}
                fullWidth
                required
                autoComplete="family-name"
              />
            </Grid>
          </Grid>

          <TextField
            label="Email address"
            type="email"
            size="small"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={!!email && !emailValid}
            helperText={!!email && !emailValid ? "Enter a valid email address." : " "}
            fullWidth
            required
            autoComplete="email"
          />

          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            size="small"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={!!password && !passwordValid}
            fullWidth
            required
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((show) => !show)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Stack spacing={0.5} sx={{ p: 1.1, borderRadius: 2, bgcolor: "#f8fafd", border: "1px solid #e3e7ee" }}>
            {passwordRules.map((rule) => (
              <Requirement key={rule.label} valid={rule.valid}>
                {rule.label}
              </Requirement>
            ))}
          </Stack>

          <Typography variant="caption" color="text.secondary">
            By creating an account, you agree to the <MuiLink component={RouterLink} to="/terms">User Agreement</MuiLink> and <MuiLink component={RouterLink} to="/privacy">Privacy Policy</MuiLink>.
          </Typography>

          <Button
            type="submit"
            variant="contained"
            size="medium"
            disabled={loading || !formValid}
            sx={{ textTransform: "none", fontWeight: 850, borderRadius: 2, py: 0.95 }}
          >
            {loading ? <Loader size={22} /> : "Agree and create account"}
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
            Already have an account? <MuiLink component={RouterLink} to="/signin">Sign in</MuiLink>
          </Typography>
        </Stack>
      </Box>
    </AuthPageFrame>
  );
};

export default SignUp;
